import React, {useEffect, useMemo} from "react"
import {useAccessToken, useIsBankUser, useIsOrgUser, useIsUnitUser, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useQueryState} from "use-location-state"
import SearchBox from "../../components/SearchBox/SearchBox"
import moment from "moment"
import {AccountStatus, AccountType, DacaStatusType, OverdraftStatus, findAccountsHandler} from "../../resources/account"
import {startCase} from "lodash"
import {ErrorDocument} from "../../resources/common"
import {Filter} from "../Filter/Filter"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {Cents} from "../../resources/transaction"
import {CurrencyRangeFilter, RangePreset} from "../Filter/CurrencyRangeFilter"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import {Bank, findBanks} from "../../resources/bank"
import {findDepositProducts} from "../../resources/depositProduct"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AccountCustomerType, AllowedAccountsColumns} from "./Accounts.types"
import AccountsTable from "./AccountsTable"

const customerTypeToFilterOptions: Record<AccountCustomerType, string> = {
	[AccountCustomerType.individual]: "Individual Customer",
	[AccountCustomerType.business]: "Business Customer",
	[AccountCustomerType.businessFBO]: "Business FBO Customer",
	[AccountCustomerType.individualWallet]: "Individual Wallet Customer",
}

interface AccountsProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedAccountsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	type?: keyof typeof AccountType
	customerId?: string
	enableSearch?: boolean
	enableOrgFilter?: boolean
	enableBankFilter?: boolean
	enableDepositProductFilter?: boolean
	enableCustomerTypeFilter?: boolean
	enableStatusFilter?: boolean
	enableDacaStatusFilter?: boolean
	enableOverdraftStatusFilter?: boolean
	enableTypeFilter?: boolean
	refreshToken?: number
	refresh?: () => void
	enableTutorial?: boolean
	title?: string
	enableExportAccounts?: boolean
	filterByOrgFBOAccount?: string
}

const accountTypeToFilterOptions: Record<AccountType, string> = {
	[AccountType.deposit]: "Deposit Account",
	[AccountType.batch]: "Batch Account",
	[AccountType.gl]: "General Ledger Account",
	[AccountType.creditGL]: "Credit General Ledger Account",
	[AccountType.credit]: "Credit Account",
	[AccountType.orgGeneralLedger]: "Lending General Ledger Account",
	[AccountType.orgLoan]: "Org Loan Account",
	[AccountType.financialBusinessFBO]: "Financial Account",
	[AccountType.wallet]: "Wallet Account",
	[AccountType.fbo]: "Org FBO Account",
	[AccountType.merchantCashAdvance]: "Merchant Cash Advance Account",
}

function filterAccountTypesByUserType(): AccountType[] {
	if (useIsUnitUser()) {
		return Object.values(AccountType)
	}

	if (useIsBankUser()) {
		return [
			AccountType.deposit,
			AccountType.batch,
			AccountType.gl,
			AccountType.credit,
			AccountType.orgGeneralLedger,
			AccountType.orgLoan,
			AccountType.financialBusinessFBO,
			AccountType.creditGL,
			AccountType.fbo,
			AccountType.wallet,
		]
	}
	if (useIsOrgUser()) {
		return [
			AccountType.deposit,
			AccountType.batch,
			AccountType.credit,
			AccountType.orgGeneralLedger,
			AccountType.orgLoan,
			AccountType.financialBusinessFBO,
			AccountType.fbo,
			AccountType.wallet,
		]
	}

	// Partner user
	return [
		AccountType.deposit,
		AccountType.batch,
		AccountType.credit,
		AccountType.orgGeneralLedger,
		AccountType.orgLoan,
		AccountType.gl,
		AccountType.creditGL,
	]
}

export function Accounts({
	limit = 25,
	includedColumns,
	enableTitle,
	fullHeight,
	type,
	customerId,
	enableSearch,
	enableStatusFilter,
	enableDacaStatusFilter,
	enableOverdraftStatusFilter,
	enableOrgFilter,
	enableBankFilter,
	enableDepositProductFilter,
	enableCustomerTypeFilter,
	enableTypeFilter,
	refreshToken,
	refresh,
	enableTutorial = false,
	title = "Accounts",
	enableExportAccounts = false,
	filterByOrgFBOAccount,
}: AccountsProps) {
	const prefix = "ac"
	const accessToken = useAccessToken()
	const userType = useUserType()

	const overdraftStatuses = new Map(
		[OverdraftStatus.Positive, OverdraftStatus.OverdrawnWithinLimit, OverdraftStatus.Negative].map((v) => [
			v,
			startCase(v),
		])
	)

	const accountTypeFilterOptions = new Map(
		filterAccountTypesByUserType().map((typeValue) => [typeValue, accountTypeToFilterOptions[typeValue]])
	)

	const customerTypeArray = [
		AccountCustomerType.individual,
		AccountCustomerType.business,
		AccountCustomerType.businessFBO,
		AccountCustomerType.individualWallet,
	]
	const customerTypeFilterOptions = new Map(
		customerTypeArray.map((typeValue) => [typeValue, customerTypeToFilterOptions[typeValue]])
	)

	const statusesOptions = new Map([AccountStatus.Open, AccountStatus.Closed, AccountStatus.Frozen].map((v) => [v, v]))
	const dacaStatusesOptions = new Map([DacaStatusType.Entered, DacaStatusType.Activated].map((v) => [v, v]))

	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [overdraftStatus, setOverdraftStatus] = useQueryState<string[]>(`${prefix}-filter[overdraftStatus]`, [])
	const [statuses, setStatuses] = useQueryState<AccountStatus[]>(`${prefix}-filter[status]`, [])
	const [dacaStatuses, setDacaStatuses] = useQueryState<DacaStatusType[]>(`${prefix}-filter[dacaStatus]`, [])
	const [fromBalance, setFromBalance] = useQueryState<Cents | "">(`${prefix}-filter[fromBalance]`, "")
	const [toBalance, setToBalance] = useQueryState<Cents | "">(`${prefix}-filter[toBalance]`, "")
	const [filteredByOrg, setFilteredByOrg] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [accountTypes, setAccountTypes] = useQueryState<(keyof typeof AccountType)[]>(`${prefix}-filter[type]`, [])
	const [customerTypes, setCustomersTypes] = useQueryState<(keyof typeof AccountCustomerType)[]>(
		`${prefix}-filter[customer_type]`,
		[]
	)
	const [filteredDepositProducts, setFilteredDepositProducts] = useQueryState<string[]>(
		`${prefix}-filter[depositProducts]`,
		[]
	)

	const {addToast} = useToasts()
	const isFiltering =
		overdraftStatus.length != 0 ||
		query != "" ||
		filteredByOrg.length != 0 ||
		statuses.length != 0 ||
		filteredBanks.length != 0
	filteredDepositProducts.length != 0

	const banks =
		userType === "unit"
			? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
			: AsyncResult.pending<Bank[], ErrorDocument>()

	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const accountTypeFilter = (
		<Filter
			title="Type"
			statuses={accountTypes}
			setStatuses={setAccountTypes}
			onFilterFunc={() => reset(limit)}
			options={accountTypeFilterOptions}
		/>
	)

	const customerTypeFilter = (
		<Filter
			title="Customer Type"
			statuses={customerTypes}
			setStatuses={setCustomersTypes}
			onFilterFunc={() => reset(limit)}
			options={customerTypeFilterOptions}
		/>
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const dacaStatusFilter = (
		<Filter
			statuses={dacaStatuses}
			setStatuses={setDacaStatuses}
			onFilterFunc={() => reset(limit)}
			options={dacaStatusesOptions}
			title="DACA Status"
		/>
	)

	const overdraftStatusFilter = (
		<Filter
			statuses={overdraftStatus}
			setStatuses={setOverdraftStatus}
			onFilterFunc={() => reset(limit)}
			options={overdraftStatuses}
			title={"Overdraft Status"}
		/>
	)

	const balanceOptions: RangePreset[] = [
		{name: "Positive", from: 1, to: ""},
		{name: "Zero", from: 0, to: 0},
		{name: "Negative", to: -1, from: ""},
	]

	const balanceFilter = (
		<CurrencyRangeFilter
			placeholder={"Balance"}
			presets={balanceOptions}
			from={fromBalance}
			setFrom={setFromBalance}
			to={toBalance}
			setTo={setToBalance}
		/>
	)

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findAccountsHandler({
				accessToken,
				offset,
				limit,
				sort: sortBy,
				types: (type && [type]) ?? accountTypes,
				searchQuery: query,
				overdraftStatus,
				status: statuses,
				dacaStatus: dacaStatuses,
				orgs: filteredByOrg,
				banks: filteredBanks,
				depositProducts: filteredDepositProducts,
				customerTypes: customerTypes,
				customerId,
				fromBalance,
				toBalance,
				requestType: "json",
				orgFBOAccount: filterByOrgFBOAccount,
				extraFields: customerId
					? {
							account: "maskedAccountNumber",
					  }
					: undefined,
			}),
		(x) => x.data.length,
		[
			query,
			refreshToken,
			sortBy,
			[
				...accountTypes,
				...overdraftStatus,
				...statuses,
				...dacaStatuses,
				...filteredByOrg,
				...filteredBanks,
				...filteredDepositProducts,
				customerTypes,
				fromBalance,
				toBalance,
			].join(","),
		],
		prefix,
		true
	)

	const depositProducts = useAsyncResult(() => findDepositProducts(accessToken, 0, 10000), [])

	const depositProductFilter = useMemo(
		() =>
			depositProducts.match(
				() => null,
				(depositProducts) => (
					<Filter
						title="Deposit Products"
						isSearchable
						setStatuses={setFilteredDepositProducts}
						statuses={filteredDepositProducts}
						onFilterFunc={() => reset(limit)}
						options={
							new Map<string, string>(
								depositProducts.data
									.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
									.map((dp) => [dp.id, dp.attributes.name])
							)
						}
						virtualized
					/>
				),
				(_) => null
			),
		[depositProducts, setFilteredDepositProducts, filteredDepositProducts.join(","), reset, limit]
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredByOrg, filteredByOrg, reset, limit),
		[orgs, setFilteredByOrg, filteredByOrg.join(","), reset, limit]
	)

	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Accounts", 500, onSearch)

	async function exportAccounts() {
		const response = await findAccountsHandler({
			accessToken,
			offset: 0,
			types: (type && [type]) ?? accountTypes,
			sort: sortBy,
			searchQuery: query,
			overdraftStatus,
			status: statuses,
			orgs: filteredByOrg,
			banks: filteredBanks,
			customerId,
			fromBalance,
			toBalance,
			requestType: "csv",
			orgFBOAccount: filterByOrgFBOAccount,
			extraFields: customerId
				? {
						account: "maskedAccountNumber",
				  }
				: undefined,
		})

		response.match(
			(d) => downloadFile("Accounts.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function toggleSort(columnName: string) {
		const sortParam = sortBy == `-${columnName}` ? columnName : `-${columnName}`
		setSortBy(sortParam)
		reset(limit)
	}

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	const filters = [
		...(enableTypeFilter && !type ? [accountTypeFilter] : []),
		...(enableCustomerTypeFilter && !type ? [customerTypeFilter] : []),
		...(enableOrgFilter ? [orgFilter] : []),
		...(enableBankFilter ? [bankFilter] : []),
		...(enableDepositProductFilter ? [depositProductFilter] : []),
		...(enableOverdraftStatusFilter ? [balanceFilter] : []),
		...(enableStatusFilter ? [statusFilter] : []),
		...(enableOverdraftStatusFilter && useIsUnitUser() ? [overdraftStatusFilter] : []),
		...(enableDacaStatusFilter ? [dacaStatusFilter] : []),
	]
	return (
		<DataTableCard className={"accounts-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={enableSearch ? searchBox : null}
				filters={filters.length === 0 ? null : filters}
				title={enableTitle ? title : null}
				exportFunc={enableExportAccounts || fullHeight ? exportAccounts : undefined}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: accounts}) => {
					return (
						<AccountsTable
							fullHeight={fullHeight}
							accounts={accounts.data}
							include={accounts.included}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							meta={accounts.meta}
							enableTutorial={enableTutorial}
							isFiltering={isFiltering}
							refresh={refresh}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
